import {
    createContext,
    useRef,
    useState,
    useCallback,
    useMemo,
    useImperativeHandle,
    useContext
} from 'react'

import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'

export const MethodsContext = createContext()

const MethodsSnackbar = ({ref}) => {
    const [snackbar, setSnackbar] = useState({
        open: false,
        text: '',
        action: {},
        loading: false
    })
    const clear = {
        open: false,
        onAccept: null
    }
    const [dialog, setDialog] = useState({
        open: false,
        text: '',
        onAccept: null
    })

    useImperativeHandle(ref, () => ({
        show(text, action, loading=false) {
            setSnackbar({
                open: true,
                text,
                action,
                loading
            })
        },

        close() {
            setSnackbar(prev => ({
                ...prev,
                open: false
            }))
        },

        ask(text, onAccept) {
            setDialog({
                open: true,
                text,
                onAccept
            })
        }
    }))

    return (
        <>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.loading ? null : 6000}
                onClose={() => snackbar.loading
                    ? null
                    : setSnackbar(prev => ({
                        ...prev,
                        open: false
                    }))
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                message={
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        {snackbar.loading &&
                            <CircularProgress
                                size={18}
                                color='inherit'
                            />
                        }

                        <Typography variant='body2'>
                            {snackbar.text}
                        </Typography>
                    </Box>
                }
                action={'text' in (snackbar?.action || {}) &&
                    <Button
                        color='inherit'
                        onClick={snackbar.action.onClick}
                    >
                        {snackbar.action.text}
                    </Button>
                }
            />
            <Dialog
                open={dialog.open}
                onClose={() => setDialog(prev => ({...prev, ...clear}))}
                fullWidth
                maxWidth='xs'
            >
                <DialogContent>
                    <DialogContentText>{dialog.text}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(prev => ({...prev, ...clear}))}>إلغاء</Button>
                    <Button onClick={() => {
                        dialog.onAccept()
                        setDialog(prev => ({...prev, ...clear}))
                    }}>حذف</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export function MethodsProvider({ children }) {
    const ref = useRef()

    const showMessage = useCallback((props) => {
        if (!props) {
            ref.current?.close()
            return
        }

        const {
            text,
            action, // {text, onClick}
            loading=false
        } = props

        ref.current?.show(text, action, loading)
    }, [])

    const ask = useCallback((text, onAccept) => {
        ref.current?.ask(text, onAccept)
    }, [])

    const value = useMemo(() => ({
        showMessage,
        ask
    }), [showMessage, ask])

    return (
        <MethodsContext.Provider value={value}>
            {children}
            <MethodsSnackbar ref={ref} />
        </MethodsContext.Provider>
    )
}
